import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core'
import React, { Fragment, useEffect, useState, useRef } from 'react'
import { FaTimesCircle } from 'react-icons/fa'
import { MdClose, MdPrint, MdFileDownload } from 'react-icons/md'
import { useReactToPrint } from 'react-to-print'
import * as XLSX from 'xlsx'
import FunkyInput from './FunkyInput'
import moment from 'moment'

    const useStyles = makeStyles(theme => ({
            dialogPaper: {
                minHeight: "99vh", minWidth: "75vw"
            },
            printContainer: {
                "@media print" :{
                    "page" : {
                        "size": "landscape",
                        "margin": "1cm"
                    }
                }
                // This class is for potential future print-specific styling of the container if needed.
                // For now, we will use the pageStyle prop in useReactToPrint for @page rules.
            },
            table: {
                border: `1px solid ${theme.palette.grey[300]}`,
            },
            tableHeaderCell: {
                backgroundColor: theme.palette.grey[200],
                color: theme.palette.common.black,
                fontWeight: 'bold',
                border: `1px solid ${theme.palette.grey[300]}`,
                lineHeight:'1.25em'
            },
            tableCell: {
                border: `1px solid ${theme.palette.grey[300]}`,
                fontSize: '0.8rem', 
                lineHeight:'1.25em'
            },
            '@global': {
                '@media print': {
                    'body, html': {
                        background: '#fff',
                        color: '#000',
                    },
                    '.printable-header': {
                        fontSize: '0.7rem !important',
                        padding: '4px 6px !important',
                    },
                    '.printable-cell': {
                        fontSize: '0.65rem !important',
                        padding: '2px 4px !important',
                        // Break long strings that have no spaces
                        wordBreak: 'break-all',
                    }
                }
            }
        }))
function cleanObject(obj, currentKey = null) {
  if (Array.isArray(obj)) {
    // Process arrays recursively, keep empty arrays
    return obj
      .map(item => cleanObject(item))
      .filter(v => v !== undefined); 
  } else if (obj && typeof obj === "object") {
    const newObj = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === "__typename") continue; // 1) eliminate __typename
      
      const cleaned = cleanObject(value, key);
      if (
        cleaned !== undefined &&
        cleaned !== null &&
        cleaned !== "" &&
        !(typeof cleaned === "object" && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0)
      ) {
        newObj[key] = cleaned;
      }
    }
    return Object.keys(newObj).length ? newObj : undefined;
  } else {
    let processedValue = obj;
    if (typeof processedValue === 'string') {
        // This cleaning logic should ONLY apply to the price field.
        if (currentKey === 'terenPret') {
            // Stage 1: Remove all non-numeric characters except for commas and periods.
            processedValue = processedValue.replace(/[^\d,.]/g, '');

            // Stage 2: Normalize decimal and thousands separators.
            if (/[.,]/.test(processedValue)) {
                const decimalMatch = processedValue.match(/([.,])(\d{2})$/);

                if (decimalMatch) {
                    const integerPart = processedValue.substring(0, decimalMatch.index);
                    const decimalPart = decimalMatch[2];
                    processedValue = `${integerPart.replace(/[.,]/g, '')},${decimalPart}`;
                } else {
                    processedValue = processedValue.replace(/[.,]/g, '');
                }
            }
        } else if (currentKey === 'terenHa') {
            // Sanitize the area field: remove non-numeric chars, then normalize decimal separator to a comma.
            processedValue = processedValue.replace(/[^\d,.]/g, ''); // Keep only digits, commas, periods
            processedValue = processedValue.replace(/\./g, ',');      // Replace all periods with commas
        }
    }
    // Remove nulls and empty strings after cleaning
    return (processedValue === null || processedValue === "" ? undefined : processedValue);
  }
}

function getUniqueTerenCfLoc(arr) {
  const set = new Set();

  arr.forEach(item => {
    if (item.anexe && item.anexe.terenLoc) {
      set.add(item.anexe.terenLoc);
    }
  });

  return Array.from(set).map(loc => ({ loc, siruta: null }));
}

export default function StatisticaModal({data, onClose}) {

    const [items, setItems] = useState(null)
    const [allItems, setAllItems] = useState(null)
    const [localitati, setLocalitati] = useState(null)
    const [filters, setFilters] = useState({
        from: moment().startOf("year").format("YYYY-MM-DD"),
        to: moment().endOf("year").format("YYYY-MM-DD"),
    })
    const [sirutaModal, setSirutaModal] = useState(null)

    const printRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: 'raport-statistica-judeteana',
        pageStyle: `
            @page {
                size: A4 landscape;
                margin: 20mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                }
            }`
    });

    const handleExportXLSX = () => {
        if (!items) return;

        const dataToExport = items.map((el, index) => ({
            'Nr.crt.': index + 1,
            'Localitatea': el?.anexe?.terenLoc?.toUpperCase(),
            'Cod localitate': el?.anexe?.terenCod,
            'Număr Carte funciara': el?.anexe?.terenCf,
            'Număr Parcela': el?.anexe?.terenParcela,
            'Aviz final MADR/DADR': el.status ==="FINALIZAT" ? 1 : 2,
            'Suprafaţa vanduta (ha)': el?.anexe?.terenHa,
            'Categoria de folosinţă': el?.anexe?.terenCategorie,
            'Preţul de vânzare (lei)': el?.anexe?.terenPret,
            'Observații': "cerere " + el?.nr + " / " + moment(el?.dt).format("DD.MM.YYYY")
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Raport");

        // Trigger the download
        XLSX.writeFile(workbook, `raport-statistic-${moment().format("DD-MM-YYYY_HH-mm")}.xlsx`);
    };

    useEffect(()=>{
        if (data) {
            let nd = data?.registru?.reduce( (acc,el) => [...acc, {...el, anexe: JSON.parse(el.anexe)} ], [])
            nd = cleanObject(nd)
            setLocalitati(getUniqueTerenCfLoc(nd))
            setAllItems(nd) // Store the complete, unfiltered dataset
            // Initially, set the visible items to the full dataset filtered by the default period
            const filteredData = nd.filter(item => moment(item.dt).isBetween(filters.from, filters.to, 'day', '[]'));
            setItems(filteredData);
        } else {
            setItems(null)
            setAllItems(null)
        }
    }, [data]) // This effect should only run when the base `data` prop changes


    useEffect(()=>{
        if (allItems && localitati) {
            // 1. Filter by date
            let processedData = allItems.filter(item => 
                moment(item.dt).isBetween(filters.from, filters.to, 'day', '[]')
            );

            // 2. Map SIRUTA codes to the filtered data
            processedData = processedData.map(item => {
                const localitateInfo = localitati.find(loc => loc.loc === item.anexe.terenLoc);
                const siruta = localitateInfo?.siruta;
                // Add siruta to the anexe object for consistency
                return siruta ? { ...item, anexe: { ...item.anexe, terenCod: siruta } } : item;
            });

            setItems(processedData);
        }
    }, [filters, allItems, localitati])

    const classes = useStyles()


    const filterHandler = name => () => {
        switch (name) {
            case "cur-year": 
                setFilters({
                    from: moment().startOf("year").format("YYYY-MM-DD"),
                    to: moment().endOf("year").format("YYYY-MM-DD")
                })
                break;
            case "prev-year": 
                setFilters({
                    from: moment().subtract(1, "Y").startOf("year").format("YYYY-MM-DD"),
                    to: moment().subtract(1, "Y").endOf("year").format("YYYY-MM-DD")
                })
                break;
            case "cur-month": 
                setFilters({
                    from: moment().startOf("M").format("YYYY-MM-DD"),
                    to: moment().endOf("M").format("YYYY-MM-DD")
                })
                break;                   
            case "prev-month": 
                setFilters({
                    from: moment().subtract(1, "M").startOf("M").format("YYYY-MM-DD"),
                    to: moment().subtract(1, "M").endOf("M").format("YYYY-MM-DD")
                })
                break;            
        }
    }
    const dtHandler = name => ev => setFilters({...filters, [name]: moment(ev.target.value).format("YYYY-MM-DD")})

    const sirutaHandler = (e,i)=> ev => {
        let nd = [...localitati]
        nd[i]['siruta'] = ev.target.value
        setLocalitati(nd)
    }
  return (
    <Dialog open = {Boolean(items)} onClose = {()=>onClose()} classes = {{paper: classes.dialogPaper}}>
        <DialogTitle>
            <Grid container alignItems='center' >
                <Grid item sm={true}>
                    RAPORT PENTRU DIRECȚIE JUDEȚEANĂ DE STATISTICĂ 
                </Grid>
                <IconButton color="primary" onClick={()=>onClose()}>
                    <FaTimesCircle size = "1em"/>
                </IconButton>

            
            </Grid>
        </DialogTitle>
        <DialogContent>

            {items && 
                <Fragment>
                    <Box sx = {{background:"lightgrey", mb:2, p:2}}>
                        <Grid container alignItems='center'>
                            <Grid item>
                                <Typography variant="caption" style={{padding:"0 10px 0 10px"}}>
                                    PERIOADA DE RAPORTARE - DE LA
                                </Typography>
                            </Grid>
                            <Grid item sm={2}>
                                <FunkyInput type = "date" value = {filters?.from} onChange = {dtHandler("from")} />
                            </Grid>
                            <Grid item>
                                <Typography variant="caption" style={{padding:"0 10px 0 10px"}} >
                                   PĂNĂ LA
                                </Typography>
                            </Grid>
                            <Grid item sm={2}>
                                <FunkyInput type = "date" value = {filters?.to} onChange = {dtHandler("to")}/>
                            </Grid>   
                            <Grid item sm = {1}  style = {{paddingRight:"10px"}}>
                                <Button  
                                    fullWidth variant='contained' color="primary" style = {{lineHeight:1, fontSize:"0.8em", margin:"10px"}}
                                    onClick = {filterHandler("cur-year")}
                                >
                                    ANUL<br/>CURENT
                                </Button>
                            </Grid>    
                            <Grid item sm = {1} style = {{paddingRight:"10px"}}>
                                <Button  
                                    fullWidth variant='contained' color="primary" style = {{lineHeight:1, fontSize:"0.8em", margin:"10px"}}
                                    onClick = {filterHandler("prev-year")}
                                >
                                    ANUL<br/>ANTERIOR
                                </Button>
                            </Grid>   
                            <Grid item sm = {1} style = {{paddingRight:"10px"}}>
                                <Button  
                                    fullWidth variant='contained' color="primary" style = {{lineHeight:1, fontSize:"0.8em", margin:"10px"}}
                                    onClick = {filterHandler("cur-month")}
                                >
                                    LUNA<br/>CURENTĂ
                                </Button>
                            </Grid>   
                            <Grid item sm = {1} style = {{paddingRight:"10px"}}>
                                <Button  
                                    fullWidth variant='contained' color="primary" style = {{lineHeight:1, fontSize:"0.8em", margin:"10px"}}
                                    onClick = {filterHandler("prev-month")}
                                >
                                    LUNA<br/>ANTERIOARĂ
                                </Button>
                            </Grid> 
                            <Grid item sm = {true}/>
                            <Grid item sm={1} align="right">
                                <Typography 
                                    variant='caption' style = {{lineHeight:'0.8', textDecoration:"underline", cursor:"pointer"}}  align="center"
                                    onClick = {()=>setSirutaModal(localitati)}
                                >
                                    COD SIRUTA
                                </Typography>
                            </Grid>
                        </Grid>
                        
                    </Box>
                    <TableContainer ref={printRef} className={classes.printContainer}>
                        <Typography className={classes.printOnly} variant='h6' align="center" style = {{padding:"10px"}}>
                            REGISTRU ARENDARE <br/> PERIOADA DE RAPORTARE: <strong>{moment(filters.from).format("DD.MM.YYYY")} - {moment(filters.to).format("DD.MM.YYYY")}</strong>
                        </Typography>
                        <Table className={classes.table} size="small">
                            <TableHead>
                                <TableRow >
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Nr.crt.</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Localitatea</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Cod<br/>localitate</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Număr<br/>Carte funciara</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Număr<br/>Parcela</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Aviz final DADR <br/> 1 = DA, 2 = NU</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Suprafaţa<br/>vanduta (ha)</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Categoria de<br/>folosinţă</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Preţul de<br/>vânzare (lei)</TableCell>
                                    <TableCell className={`${classes.tableHeaderCell} printable-header`} align="center">Observații</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items?.map( (el, index) => (
                                    <TableRow key={index}>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">{index+1}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">{el?.anexe?.terenLoc.toUpperCase()}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">{el?.anexe?.terenCod || 'N/A'}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">{el?.anexe?.terenCf}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">{el?.anexe?.terenParcela}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">
                                            {el.status ==="FINALIZAT" ? 1 : 2}

                                        </TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="right" style = {{paddingRight:"10px"}}>{el?.anexe?.terenHa}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="center">{el?.anexe?.terenCategorie}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="right">{el?.anexe?.terenPret}</TableCell>
                                        <TableCell className={`${classes.tableCell} printable-cell`} align="right">
                                            dosar {el?.nr} / {moment(el?.dt).format("DD.MM.YYYY")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* {console.log("items:", items)} */}
                    {/* <pre>{JSON.stringify(items, null, 3)}</pre> */}
            
            
                </Fragment>
            }
        </DialogContent>
        <DialogActions>
            <Button variant='contained' color="primary" startIcon={<MdPrint/>} onClick={handlePrint}>
                    PRINT
            </Button>
            <Button variant='contained' color="primary" startIcon={<MdFileDownload/>} onClick={handleExportXLSX}>
                    Export XLSX
            </Button>
            <Button variant='outlined' color="primary" startIcon={<MdClose/>} onClick={onClose}>
                    INCHIDE
            </Button>
        </DialogActions>
        {sirutaModal && <Dialog open ={Boolean(sirutaModal)} onClose={()=>setSirutaModal(false)} >
            <DialogTitle >
                <Grid container alignItems='center'>
                    
                    <Grid item sm={true}>
                        <Typography variant='body1' style = {{fontWeight:900}}>
                        SETARE CODURI SIRUTA
                        </Typography>
                </Grid>    
                <IconButton size="small" color="secondary" onClick={()=>setSirutaModal(null)}><MdClose/></IconButton>
                
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Localitatea</TableCell>
                            <TableCell>Codul SIRUTA</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {localitati.map( (e, i)=> (
                            <TableRow key={i}>
                                <TableCell>{e.loc}</TableCell>
                                <TableCell>
                                    <FunkyInput value = {e.siruta} onChange={sirutaHandler(e, i)}/>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

            </DialogContent>
            
            </Dialog>}
    </Dialog>
  )
}
